import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight } from 'lucide-react';
import { storeUrl } from './storeBase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Catalog mega-menu — hover-triggered dropdown on the Catalog nav item.
 * Renders the storefront's curated Featured Collections as chunky outlined
 * pills (the same arcade button styling as the rest of the storefront).
 */
const MegaMenuCatalog = ({ active }) => {
  const [open, setOpen] = useState(false);
  const [collections, setCollections] = useState(null);
  const [loading, setLoading] = useState(false);
  const closeTimer = useRef(null);
  const location = useLocation();

  // Close on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const fetchOnce = async () => {
    if (collections !== null || loading) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/storefront/featured-collections`);
      setCollections(r.data?.items || []);
    } catch (e) {
      setCollections([]);
    } finally {
      setLoading(false);
    }
  };

  const openMenu = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpen(true);
    fetchOnce();
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 140);
  };

  const hasCollections = Array.isArray(collections) && collections.length > 0;
  const showPanel = loading || hasCollections;

  return (
    <div
      className="sf-mega-wrap"
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
      onFocus={openMenu}
      onBlur={scheduleClose}
      data-testid="sf-catalog-mega"
    >
      <Link
        to={storeUrl('/catalog')}
        className={`sf-label text-xs ${active ? 'text-white' : 'hover:text-white text-zinc-400 transition-colors'}`}
        data-testid="sf-nav-catalog"
        aria-haspopup="true"
        aria-expanded={open}
      >
        Catalog
      </Link>

      <div
        className={`sf-mega-panel ${open && showPanel ? 'open' : ''}`}
        role="menu"
        data-testid="sf-mega-panel"
      >
        <div className="sf-mega-inner">
          <div className="sf-mega-header">
            <span className="sf-label" style={{ color: 'var(--sf-pink)' }}>Browse</span>
            <Link
              to={storeUrl('/catalog')}
              className="sf-mega-viewall"
              data-testid="sf-mega-viewall"
            >
              All products <ArrowRight size={12} />
            </Link>
          </div>

          {loading && (
            <div className="sf-mega-empty" data-testid="sf-mega-loading">Loading…</div>
          )}

          {!loading && hasCollections && (
            <div className="sf-mega-pills" data-testid="sf-mega-pills">
              {collections.map((c) => (
                <Link
                  key={c.handle}
                  to={storeUrl(`/catalog?collection=${encodeURIComponent(c.handle)}`)}
                  className="sf-mega-pill"
                  data-testid={`sf-mega-pill-${c.handle}`}
                >
                  <span className="sf-mega-pill-label">{c.title}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MegaMenuCatalog;
